import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { getSalesPreBookCartItems } from '../apis/sales/preBookApis';
import { PreBookHeader } from '../apis/retailer/preBookApis';
import { RootState } from '../store';

/**
 * Sales pre-book cart (ordering on behalf of the selected customer).
 *
 * Holds the cart of ONE pre-book at a time for the customer selected in the
 * sales session. Not persisted. The normal `salesCart` slice is untouched.
 */

interface SalesPreBookCartState {
  preBookId: number | null;
  customerId: number | null;
  preBook: PreBookHeader | null;
  items: any[];
  loading: boolean;
  error: string | null;
  count: number;
  totalItems: number;
  totalAmountWithTax: number;
  userItemLimitQty: number | null;
  userLimitMinOrderAmount: number | null;
  totalAmount: number;
}

const initialState: SalesPreBookCartState = {
  preBookId: null,
  customerId: null,
  preBook: null,
  items: [],
  loading: false,
  error: null,
  count: 0,
  totalItems: 0,
  totalAmountWithTax: 0,
  userItemLimitQty: null,
  userLimitMinOrderAmount: null,
  totalAmount: 0,
};

// Fetch the cart of one pre-book for the selected customer (S5)
export const fetchSalesPreBookCartItems = createAsyncThunk(
  'salesPreBookCart/fetchSalesPreBookCartItems',
  async (preBookId: number | string, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const customerId = state.auth.selectedCustomer?.C_Number;
      if (!customerId) {
        throw new Error('Customer ID not found');
      }
      const response: any = await getSalesPreBookCartItems(preBookId, customerId.toString());
      return { preBookId: Number(preBookId), customerId: Number(customerId), data: response.data };
    } catch (error: any) {
      return rejectWithValue({ message: error?.response?.data?.message || error?.message || 'Failed to fetch pre-book cart items', status: error?.response?.status });
    }
  }
);

const salesPreBookCartSlice = createSlice({
  name: 'salesPreBookCart',
  initialState,
  reducers: {
    setSalesPreBookCartCount: (state, action: PayloadAction<number>) => {
      state.count = action.payload;
    },
    clearSalesPreBookCart: (state) => {
      state.items = [];
      state.count = 0;
      state.totalItems = 0;
      state.totalAmountWithTax = 0;
      state.totalAmount = 0;
    },
    resetSalesPreBookCart: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSalesPreBookCartItems.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        const nextId = Number(action.meta.arg);
        if (state.preBookId !== nextId) {
          state.preBookId = nextId;
          state.preBook = null;
          state.items = [];
          state.count = 0;
          state.totalItems = 0;
          state.totalAmountWithTax = 0;
          state.totalAmount = 0;
        }
      })
      .addCase(fetchSalesPreBookCartItems.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload?.data;
        state.preBookId = action.payload?.preBookId ?? state.preBookId;
        state.customerId = action.payload?.customerId ?? state.customerId;
        state.preBook = payload?.preBook ?? state.preBook;
        state.items = payload?.finalCartItems || [];
        state.count = state.items.length;
        state.totalItems = payload?.totalItems || 0;
        state.totalAmountWithTax = payload?.totalAmountWithTax || 0;
        state.userItemLimitQty = payload?.userItemLimitQty || null;
        state.userLimitMinOrderAmount = payload?.userLimitMinOrderAmount || null;
        state.totalAmount = payload?.totalAmount || 0;
      })
      .addCase(fetchSalesPreBookCartItems.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as any)?.message || action.error.message || 'Failed to fetch pre-book cart items';
      });
  },
});

export const { setSalesPreBookCartCount, clearSalesPreBookCart, resetSalesPreBookCart } = salesPreBookCartSlice.actions;

export default salesPreBookCartSlice.reducer;
